import { useCallback, useEffect, useState } from 'react';

import {
  isPasskeySupported,
  isSocialOnlyUserFromToken,
} from '@splitfiesta/platform-web/auth/cognito';

import { usePasskeysQuery } from '@/hooks/queries/usePasskeys';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { useAuthStore } from '@/stores/authStore';

const PASSKEY_PROMPT_DISMISSED_KEY = 'passkey_prompt_dismissed';
const PASSKEY_PROMPT_DELAY = 1500; // Show prompt after 1.5s delay

interface UsePasskeyPromptReturn {
  showPrompt: boolean;
  openPrompt: () => void;
  closePrompt: () => void;
  shouldPromptUser: boolean;
}

/**
 * Hook to manage passkey registration prompt.
 * Shows prompt after login/registration if:
 * - Browser supports passkeys
 * - User is logged in with email/password (NOT social login)
 * - User has no passkeys registered
 * - User hasn't dismissed the prompt this session
 */
export function usePasskeyPrompt(): UsePasskeyPromptReturn {
  const [showPrompt, setShowPrompt] = useState(false);
  const user = useAuthStore(state => state.user);
  const idToken = useAuthStore(state => state.idToken);
  const { isOnline } = useNetworkStatus();

  // Check if user logged in via social provider (Google/Apple)
  // Social users already have passwordless login, no need to prompt for passkeys
  const isSocialUser = idToken ? isSocialOnlyUserFromToken(idToken) : false;

  // Only fetch passkeys if user is logged in, passkeys are supported, and NOT a social user
  const shouldFetch = !!user && isPasskeySupported() && !isSocialUser;
  const { data: passkeys = [], isLoading } = usePasskeysQuery(shouldFetch);

  // Check if we should prompt the user
  // Don't prompt when offline - we can't reliably check passkeys status
  const hasNoPasskeys = !isLoading && passkeys.length === 0;
  const promptDismissed =
    sessionStorage.getItem(PASSKEY_PROMPT_DISMISSED_KEY) === 'true';
  const shouldPromptUser =
    isOnline && shouldFetch && hasNoPasskeys && !promptDismissed && !isLoading;

  const openPrompt = useCallback(() => {
    if (shouldPromptUser) {
      setShowPrompt(true);
    }
  }, [shouldPromptUser]);

  const closePrompt = useCallback(() => {
    setShowPrompt(false);
    // Mark as dismissed for this session
    sessionStorage.setItem(PASSKEY_PROMPT_DISMISSED_KEY, 'true');
  }, []);

  return {
    showPrompt,
    openPrompt,
    closePrompt,
    shouldPromptUser,
  };
}

/**
 * Hook to auto-show passkey prompt after a delay.
 * Use this in components that render after successful login/registration.
 */
export function useAutoPasskeyPrompt(): UsePasskeyPromptReturn {
  const promptState = usePasskeyPrompt();
  const { shouldPromptUser, openPrompt } = promptState;

  useEffect(() => {
    if (shouldPromptUser) {
      const timer = setTimeout(() => {
        openPrompt();
      }, PASSKEY_PROMPT_DELAY);

      return () => clearTimeout(timer);
    }
  }, [shouldPromptUser, openPrompt]);

  return promptState;
}
